import React, { useState, useEffect } from "react";
import { FaBell, FaUser, FaCog, FaSignOutAlt, FaPen, FaPencilAlt } from "react-icons/fa";
import { IoMdClose } from "react-icons/io";
import "../styles/Sidebar.css";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Loader from "./Loader";
import { FaSun, FaMoon } from "react-icons/fa";
import { useTheme } from "./ThemeContext";
import { ensureValidToken, clearTokens, isOfflineMode } from "../utils/authService";

const Sidebar = ({ isOpen, toggleSidebar }) => {
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [profileName, setProfileName] = useState("");
  const [profileNumber, setProfileNumber] = useState("");
  const [profileImage, setProfileImage] = useState("");
  const [profileId, setProfileId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasUnreadNotifications, setHasUnreadNotifications] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  // Fetch user profile data and notifications
  useEffect(() => {
    const fetchData = async () => {
      // Skip API calls when offline
      if (isOfflineMode()) {
        return;
      }

      try {
        const accessToken = await ensureValidToken();
        if (!accessToken) {
          console.error("Access token not found.");
          return;
        }

        // Fetch profile
        const profileResponse = await axios.get("https://api.nexcard.co.in/api/profile/", {
          headers: { Authorization: `Bearer ${accessToken}` }
        });

        if (profileResponse.data.results.length > 0) {
          const userProfile = profileResponse.data.results[0];
          setProfileId(userProfile.id);
          setProfileName(userProfile.full_name || "");
          setProfileNumber(userProfile.phone_number || "");
          if (userProfile.profile_image) {
            setProfileImage(userProfile.profile_image);
          }
        }

        // Fetch notifications
        const notificationsResponse = await axios.get(
          "https://api.nexcard.co.in/api/cards/request/invitations/",
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        const hasUnread = notificationsResponse.data.data?.some(
          notification => !notification.is_accepted
        );
        setHasUnreadNotifications(hasUnread);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
    // Set up polling for new notifications (every 60 seconds) - only when online
    const pollInterval = setInterval(() => {
      if (!isOfflineMode()) {
        fetchData();
      }
    }, 60000);
    return () => clearInterval(pollInterval);
  }, []);

  const handleNotificationClick = () => {
    setHasUnreadNotifications(false);
    navigate("/notification");
  };

  const handleProfileClick = () => {
    setIsProfileModalOpen(true);
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file || !profileId) return;

    if (isOfflineMode()) {
      alert("You are offline. Updating profile picture is disabled in offline mode.");
      return;
    }
    
    setIsLoading(true);
    setError(null);
    try {
      const accessToken = await ensureValidToken();
      if (!accessToken) {
        navigate("/login");
        return;
      }
      const formData = new FormData();
      formData.append("profile_image", file);

      const response = await axios.patch(
        `https://api.nexcard.co.in/api/profile/${profileId}/`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.profile_image) {
        setProfileImage(response.data.profile_image);
      }
    } catch (error) {
      setError("Failed to update profile picture. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleWriteToUs = (e) => {
    e.preventDefault();
    const emailAddress = "enquiry@nexcard.co.in";
    const subject = "Customer Support Inquiry";
    const emailUrl = `mailto:${emailAddress}?subject=${encodeURIComponent(subject)}`;
    
    // Try opening in new tab first
    const newWindow = window.open(emailUrl, "_blank");
    
    // Fallback if popup is blocked
    setTimeout(() => {
      if (!newWindow || newWindow.closed) {
        window.location.href = emailUrl;
      }
    }, 300);
  };

  const defaultImage = "https://www.pngitem.com/pimgs/m/78-786293_1240-x-1240-0-avatar-profile-icon-png.png";

  return (
    <>
      {isOpen && <div className="overlay1" onClick={toggleSidebar}></div>}

      <div className={`sidebar ${isOpen ? "open" : ""}`}>
        <div className="sidebar-header1">
          <div className="profile1">
            <img src={profileImage || defaultImage} alt="Profile" className="profile-img1" />
            <div className="profile-info1">
              <h3>{profileName}</h3>
              <p>{profileNumber}</p>
            </div>
          </div>
          <button className="close-btn" onClick={toggleSidebar}>
            <IoMdClose className="close-icon1" />
          </button>
        </div>

        <ul className="sidebar-menu1">
          <li>
            <button className="sidebar-btn" onClick={handleProfileClick}>
              <FaUser className="icon1" /> Profile
            </button>
          </li>
          <li>
            <button className="sidebar-btn" onClick={handleNotificationClick}>
              <FaBell className="icon1" /> 
              {hasUnreadNotifications && <span className="notification-dot-side"></span>}
              Notification
            </button>
          </li>
          <li>
            <button className="sidebar-btn" onClick={() => navigate("/settings")}>
              <FaCog className="icon1" /> Settings
            </button>
          </li>
          <li>
            <button className="sidebar-btn" onClick={handleWriteToUs}>
              <FaPen className="icon1" /> Write to Us
            </button>
          </li>
          <li>
            <div className="small-toggle">
              <input
                type="checkbox"
                id="theme-switch"
                checked={theme === 'dark'}
                onChange={toggleTheme}
              />
              <label htmlFor="theme-switch" className="switch">
                <span className="slider">
                  {theme === 'dark' ? <FaMoon size={14} /> : <FaSun size={14} />}
                </span>
              </label>
            </div>
          </li>
          <li>
            <button className="sidebar-btn" onClick={() => {
              clearTokens();
              navigate("/login");
            }}>
              <FaSignOutAlt className="icon1" /> Logout
            </button>
          </li>
        </ul>
      </div>

      {/* Profile Modal */}
      {isProfileModalOpen && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Profile</h2>
              <button onClick={() => setIsProfileModalOpen(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="profile-image-edit">
                <img
                  src={profileImage || defaultImage}
                  alt="Profile"
                  className="profile-img"
                />
                <label htmlFor="profile-image-upload" className="edit-icon">
                  {isLoading ? <Loader small /> : <FaPencilAlt />}
                </label>
                <input
                  type="file"
                  id="profile-image-upload"
                  accept="image/*"
                  onChange={handleImageChange}
                  style={{ display: "none" }}
                  disabled={isLoading}
                />
              </div>
              {error && <p className="error-message">{error}</p>}
              <div className="form-group">
                <h4 className="profile-name" style={{textAlign:"center"}}>{profileName}</h4>
              </div>
              <div className="form-group">
                <h4 className="profile-number" style={{textAlign:"center"}}>{profileNumber}</h4>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar;